import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { normalizeTags } from "@/tags/values"
import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
  type TransportRequestError,
} from "@/transport"
import type { UseQueryResult } from "@tanstack/react-query"

import type { GetTestSetVersionsResponse } from "@/types_api/backend/transport/get_test_set_versions_response"
import type { Records as ListTestCasesTransportRecords } from "@/types_api/backend/transport/list_test_cases_transport_response"
import type { ListTestSetsTransportRequest } from "@/types_api/backend/transport/list_test_sets_transport_request"
import type { TestSetMetadataRequest } from "@/types_api/backend/transport/test_set_metadata_request"
import type { TestSetMetadataResponse } from "@/types_api/backend/transport/test_set_metadata_response"
import type { TestSetRecordsRequest } from "@/types_api/backend/transport/test_set_records_request"
import type { TestSetRecord } from "@/types/api/TestSetRecord"

import {
  isPersistedTestSetId,
  mapMediaPreviewUrlInputToTransport,
  normalizeTestCaseRecord,
  normalizeTestSetMetadataRecord,
  type GetMediaPreviewUrlInput,
} from "../utils/testSetTransportMapping"

interface UseTestSetsListOptions<T> {
  enabled?: boolean
  select?: (data: TestSetRecord[]) => T
  tagFilters?: string[]
}

interface TestSetMetadataIndexData {
  hasMore: boolean
  nextCursor?: number
  testSets: TestSetRecord[]
  totalCount?: number
}

type TestSetVersionsResult = UseQueryResult<
  GetTestSetVersionsResponse,
  TransportRequestError
> & {
  versions: GetTestSetVersionsResponse["versions"]
}

export interface CoverageCheckResult {
  covered: boolean
  matching_test_case: {
    id: string
    index: number
    input_str: string
  } | null
  reason: string
  suggested_description?: string | null
  suggested_scenario?: string | null
  suggested_expected_behaviors?: string[]
}

export function useTestSetsListTransport<T = TestSetRecord[]>(
  options?: UseTestSetsListOptions<T>,
) {
  const enabled = options?.enabled ?? true
  const normalizedTagFilters = useMemo(
    () => normalizeTags(options?.tagFilters ?? []),
    [options?.tagFilters],
  )
  const request = useMemo<ListTestSetsTransportRequest>(
    () =>
      normalizedTagFilters.length > 0
        ? {
            tag_filters:
              normalizedTagFilters as ListTestSetsTransportRequest["tag_filters"],
          }
        : {},
    [normalizedTagFilters],
  )
  const query = useTransportQuery({
    operation: "listTestSets",
    request: request as never,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      // Test-set creation invalidates this query while it's unmounted (e.g.
      // from /test-sets/create), which only marks it stale rather than
      // refetching. Override CACHE_PROFILES.lookup's refetchOnMount so
      // remounting (navigating back) picks up the new data immediately.
      refetchOnMount: true,
    },
  })

  const data = useMemo(() => {
    if (!query.data) {
      return undefined
    }

    const testSets = (query.data.test_sets ?? []).map(
      normalizeTestSetMetadataRecord,
    )
    return options?.select ? options.select(testSets) : (testSets as T)
  }, [options, query.data])

  return {
    ...query,
    data,
  }
}

export function useTestSetMetadataIndexTransport(
  request: TestSetMetadataRequest,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true
  const query = useTransportQuery({
    operation: "testSetMetadata",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
  })

  const data = useMemo<TestSetMetadataIndexData | undefined>(() => {
    if (!query.data) {
      return undefined
    }

    const testSets = (query.data.test_sets ?? []).map(
      normalizeTestSetMetadataRecord,
    )

    return {
      hasMore: query.data.has_more ?? false,
      nextCursor: query.data.next_cursor ?? undefined,
      testSets,
      totalCount: query.data.total_count ?? undefined,
    }
  }, [query.data])

  return {
    ...query,
    data,
  }
}

export function useTestSetMetadataTransport(
  testSetId: string,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true
  return useTransportQuery({
    operation: "testSetMetadata",
    request: { id: testSetId },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: enabled && isPersistedTestSetId(testSetId),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      select: (data: TestSetMetadataResponse) => ({
        metadata: data.metadata
          ? normalizeTestSetMetadataRecord(data.metadata)
          : null,
      }),
    },
  })
}

export function useTestSetRecordsTransport(
  request: TestSetRecordsRequest,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true
  return useTransportQuery({
    operation: "testSetRecords",
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: enabled && isPersistedTestSetId(request.test_set_id),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      select: (data) => data.records.map(normalizeTestCaseRecord),
    },
  })
}

export function useTestSetVersionsTransport(
  testSetId: string | null,
  options?: { enabled?: boolean },
): TestSetVersionsResult {
  const resolvedId = testSetId ?? ""
  const enabled = (options?.enabled ?? true) && isPersistedTestSetId(resolvedId)
  const query = useTransportQuery({
    operation: "getTestSetVersions",
    request: { test_set_id: resolvedId },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
    },
  })
  return {
    ...query,
    versions: query.data?.versions ?? [],
  }
}

export function useListTestCasesTransport(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true
  return useTransportQuery<"listTestCases", ListTestCasesTransportRecords>({
    operation: "listTestCases",
    request: {},
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      select: (data) => data.records,
    },
  })
}

export function useTestSetMetadataKeysTransport(
  request: { testSetId?: string | null },
  options?: { enabled?: boolean; staleTime?: number },
) {
  const enabled = options?.enabled ?? true
  return useTransportQuery({
    operation: "testSetMetadataKeys",
    request: {
      test_set_id: request.testSetId || null,
    },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      staleTime: options?.staleTime,
      select: (data) => data.keys,
    },
  })
}

export function useCreateTestSetTransport() {
  return useTransportMutation("createTestSet")
}

export function useAddTestSetRowsTransport() {
  return useTransportMutation("addTestSetRows")
}

export function useDeleteTestSetRowTransport() {
  return useTransportMutation("deleteTestSetRow")
}

export function useDeleteTestSetTransport() {
  return useTransportMutation("deleteTestSet")
}

export function useUpdateTestSetTransport() {
  return useTransportMutation("updateTestSet")
}

export function useSaveTestSetEditorStateTransport() {
  return useTransportMutation("saveTestSetEditorState")
}

export function useGetMediaUploadUrlTransport() {
  return useTransportMutation("getTestCaseMediaUploadUrl")
}

export function useGetMediaPreviewUrlTransport(
  input: GetMediaPreviewUrlInput,
  options?: { enabled?: boolean; retry?: boolean | number; staleTime?: number },
) {
  const enabled = options?.enabled ?? true
  return useTransportQuery({
    operation: "getTestCaseMediaPreviewUrl",
    request: mapMediaPreviewUrlInputToTransport(input),
    options: {
      ...CACHE_PROFILES.standard,
      enabled: enabled && Boolean(input.testCaseId),
      retry: options?.retry ?? false,
      staleTime: options?.staleTime,
    },
  })
}

export function useDuplicateTestSetTransport() {
  return useTransportMutation("duplicateTestSet")
}

export function useAddTestSetAgentAssociationsTransport() {
  return useTransportMutation("addTestSetAgentAssociations")
}

export function useRemoveTestSetAgentAssociationTransport() {
  return useTransportMutation("removeTestSetAgentAssociation")
}

export function useRevertTestSetVersionTransport() {
  return useTransportMutation("revertTestSetVersion")
}

/**
 * Revalidates the test-set catalog for a change this tab's query cache cannot
 * see: a creation form opened in another tab invalidates that tab's cache, not
 * this one, and the catalog refetches on neither mount nor focus.
 */
export function useRevalidateTestSetsCatalog() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    () => void transportQueryUtils.invalidateOperation("listTestSets"),
    [transportQueryUtils],
  )
}

export function useInvalidateTestSetTransportQueries() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(async () => {
    await Promise.all([
      transportQueryUtils.invalidateOperation("listTestSets"),
      transportQueryUtils.invalidateOperation("testSetMetadata"),
      transportQueryUtils.invalidateOperation("testSetRecords"),
      transportQueryUtils.invalidateOperation("getTestSetVersions"),
      transportQueryUtils.invalidateOperation("listTestCases"),
      transportQueryUtils.invalidateOperation("getTestSetAgentAssociations"),
      transportQueryUtils.invalidateOperation("getAgent"),
      transportQueryUtils.invalidatePaginatedOperation("listAgents"),
      transportQueryUtils.invalidatePaginatedOperation("listAllAgents"),
    ])
  }, [transportQueryUtils])
}
